
/**
 * Agent Brains section 9's scenario map, and the router that reads it.
 *
 * Section 8 fills slot [4] with "ONE lead framework's moves for this
 * scenario". Nothing could fill it, because nothing in the repo decided what
 * scenario a message was. `artifact_kind` has carried a `scenario_map` value
 * since the first migration with no producer and no reader.
 *
 * TWO RULES SHAPE EVERY DECISION BELOW.
 *
 * Precision beats recall (section 0). A router that always picks something
 * picks wrong silently, and a wrong scenario is a wrong strategy applied
 * confidently, with the wrong gates armed. An unrecognised message routes to
 * `unknown`, which section 9 assigns to class D: a human is in the loop.
 * That is a worse experience than a correct auto-route and a much better one
 * than a confident mistake.
 *
 * The map's class D rows are T3 (CLAUDE.md section 3: "Class D scenario rows:
 * complaints, fraud-suspect, unknown"). They are constants here, not
 * configuration, and auto-learn cannot propose a change to them. Section 9
 * makes the rest T1-tunable, which is why the map is versioned: a router that
 * changes without a version is a router whose past decisions cannot be
 * explained.
 *
 * HONEST LIMIT (TA-77): these are keyword rules read against a synthetic
 * corpus. On real DMs they will route a fair number of messages to `unknown`.
 * That is the designed failure direction, but the recall number is unmeasured
 * until the real inbox capture is labelled, and it must be measured before any
 * scenario graduates.
 */

import type { GraduationClass } from "./types";

/**
 * v2 is decision list item 56's ready-made widening (TA-413 addendum), the
 * first rule change to move the string. v1 covered the original map and
 * TA-315's restored rows, which kept it (OPEN-FOR-KABIR item 23). A
 * `routing.decided` row written under v1 was routed by a ready-made row that
 * could not read "do you still have" or "saw it on your story".
 *
 * v3 is TA-464: a message that reports damage no longer reaches the ready-made
 * row, and no longer inherits its thread's row. A v2 row may be a complaint
 * that was read as a purchase.
 *
 * v4 is decision list item 88 (a), Kabir's typed "fix" of 2026-09-29: the
 * complaint row reads "not happy" and its forms, and a piece not as described
 * ("smaller than advertised") is a damage report. A v3 row may be a "not
 * happy" complaint that inherited a purchase.
 *
 * v5 is decision list item 91 (a), Kabir's typed "91a" of 2026-09-29: a phone
 * keyboard's apostrophe (U+2018, U+2019, U+02BC) reads as a typed one before
 * any row is tried. A v4 row may be a damage report typed on a phone that
 * inherited a purchase, or a phone's "can't make it" or "y'all" that missed
 * its row.
 *
 * v6 is decision list item 93 (a), Kabir's typed "93a" of 2026-09-30: the
 * damage guard reads "stone's loose" and "my finger turned green", the two
 * wordings of its own reports it missed. A v5 row may be one of them that
 * inherited a purchase. Fit and size were left alone.
 *
 * v7 is item RSCH (loop f, 2026-10-03): the consult_confirm row reads
 * "reschedule", "rescheduling", "rescheduled" and "reschedules", with or
 * without the hyphen. The stem was in a group closed by \b, so none of them
 * matched and a customer who asked to reschedule routed `unknown`. A v6 row
 * may be a reschedule request that routed to a human as unknown.
 *
 * v8 2026-10-07, custom vocabulary widened (four stems), measured +2 recall
 * (custom 28 to 30 of 84), cross-routing unchanged: custom claims on other
 * labels 4, ready_made figures identical to v7 (loop o O2 and its fix 1,
 * decision row 248 item 2). The widening is request-shaped, and the custom row
 * now skips a message that reports damage (TA-464). The custom row
 * also reads "customise" and its forms, "have it made", "made for me",
 * "my own design", engrave and monogram words, and "can you make". A v7 row
 * may be one of these that routed to a human as unknown.
 *
 * v10 is TA-1355 D4 and D8, Kabir's typed "fix" of 2026-10-17: the damage
 * guard lets a buyer's trust question about fakes go ("is lab basically
 * fake?", see `claimsFake`), and the repair row's resize reads a change of
 * size ("size 6, needs to go to a 7", "go up a size"). The educated buyer row
 * skips a damage report, as the custom and ready-made rows do. A v9 row may be
 * one of the first two that routed to a human as unknown, or a damage report
 * naming its stone that routed to the debate row. A fake word after a
 * customer turn that names a purchase is still damage, as in v9
 * (`reportsDamageInThread`).
 */
export const SCENARIO_MAP_VERSION = "scenario-map-v10";

export type Scenario =
  | "repair_inquiry"
  | "ready_made_purchase"
  | "custom_engagement"
  | "price_challenge"
  | "educated_buyer_debate"
  | "complaint_warranty"
  | "ghost_revival"
  | "post_purchase_care"
  | "consult_confirm"
  | "review_ask"
  | "vendor_spam"
  | "fraud_suspect"
  | "unknown";

/**
 * Section 9's Class column (TA-315). The four graduation classes, plus the
 * one cell in the table that is not a class at all: vendor spam reads "n/a",
 * because a row that generates nothing has no send to graduate. It is a
 * distinct value rather than `null` so a log line or an event payload says
 * "not applicable" instead of reading as "not recorded", which is a different
 * claim.
 */
export type ScenarioClass = GraduationClass | "n/a";

export interface ScenarioRow {
  /** Section 9's lead framework. Exactly one per scenario, never a blend. */
  readonly leadFramework: string;
  readonly graduationClass: ScenarioClass;
  readonly specialGates: readonly string[];
  /** False for the rows section 9 marks "no generation". */
  readonly generates: boolean;
}

/**
 * Section 9's table: every ROW of it since TA-315, diffed against
 * `scenarioMap.vault.ts` (the same table as data) by
 * `scenarioMap.vault.test.ts` on every run. This comment used to read "column
 * for column" while the map was missing two rows and carried the wrong class
 * on a third, which is exactly the claim a test has to hold rather than a
 * comment. So, exactly what that test holds (TA-315, skeptic): the row keys
 * and the class on each row, cell for cell. `leadFramework` is held only to
 * being a nonempty string, because the two files word it differently on
 * purpose (the vault's arrows are ASCII here), so a slip in transcribing a
 * framework would not be caught. Still not every COLUMN either: section 9's
 * "Key skills loaded" has no
 * field here, because no skill library exists to load one from (`playcard.ts`,
 * TA-78). It is transcribed in the vault copy so the data is in the repo when
 * something can finally read it.
 */
export const SCENARIO_MAP: Readonly<Record<Scenario, ScenarioRow>> = {
  repair_inquiry: {
    leadFramework: "repair-triage intake",
    graduationClass: "A",
    specialGates: [],
    generates: true,
  },
  ready_made_purchase: {
    leadFramework: "SPIN-light into CLOSER",
    graduationClass: "B",
    specialGates: [],
    generates: true,
  },
  custom_engagement: {
    leadFramework: "SPIN",
    graduationClass: "C",
    specialGates: ["vip_check_on_entry"],
    generates: true,
  },
  price_challenge: {
    leadFramework: "CLOSER-E",
    graduationClass: "C",
    // T3, hard on. The cap is not a suggestion the framework may trade away.
    specialGates: ["discount_cap"],
    generates: true,
  },
  educated_buyer_debate: {
    leadFramework: "Challenger",
    graduationClass: "C",
    specialGates: ["compliance_leads"],
    generates: true,
  },
  complaint_warranty: {
    leadFramework: "HEARD",
    graduationClass: "D",
    specialGates: ["high_escalation_bias", "vip_to_human"],
    generates: true,
  },
  /**
   * Section 9's two OUTBOUND rows, restored by TA-315. Neither is a reading
   * of an inbound message: one reactivates a thread that went quiet, the
   * other opens one after a sale. No rule in `RULES` below emits either, so
   * `routeScenario` and `routeConversation` cannot return them and no live
   * message changes destination or framework because they exist. They become
   * reachable only when something that STARTS a conversation builds the route
   * itself, and nothing in this repo does that yet.
   */
  ghost_revival: {
    leadFramework: "draft-outreach sequencing",
    graduationClass: "B",
    // Section 9's gate cell, verbatim: "frequency caps". Caps are enforced
    // globally by the send arbiter (CLAUDE.md section 7), never per agent and
    // never by this row; the gate id is the reminder, not the mechanism.
    specialGates: ["frequency_caps"],
    generates: true,
  },
  post_purchase_care: {
    leadFramework: "clienteling",
    // Section 9's class cell is a RANGE, "A" en dash "B". Encoded at the
    // stricter end: B is ~100 drafts at 97% where A is ~50 at 95%, and an
    // unratified range resolves the careful way (CLAUDE.md section 0). The
    // literal cell is kept in `scenarioMap.vault.ts`, so nothing is lost.
    // Kabir to rule which end section 9 meant (TA-315).
    graduationClass: "B",
    specialGates: ["realism_timing"],
    generates: true,
  },
  consult_confirm: {
    leadFramework: "commitment-consistency",
    graduationClass: "A",
    specialGates: [],
    generates: true,
  },
  review_ask: {
    leadFramework: "peak-end",
    graduationClass: "B",
    specialGates: ["solicitation_compliance_leads"],
    generates: true,
  },
  vendor_spam: {
    leadFramework: "router: label and archive",
    // Section 9's class cell is "n/a", and this row read "A" until TA-315:
    // the most permissive bar in the table, on the one row that never sends
    // anything. "n/a" is carried as itself because no A to D value means "no
    // bar": "D" would add a fourth row to a T3 list that names exactly three
    // (CLAUDE.md section 3) and would trip `routeDestination.ts`'s class D
    // invariant, since section 9 archives vendor spam rather than handing it
    // to a human.
    graduationClass: "n/a",
    specialGates: [],
    // Section 9, verbatim: "none (no generation)".
    generates: false,
  },
  fraud_suspect: {
    leadFramework: "fraud-radar",
    graduationClass: "D",
    specialGates: ["freeze_and_human", "never_accuse"],
    generates: false,
  },
  unknown: {
    leadFramework: "none",
    graduationClass: "D",
    specialGates: ["human_always"],
    generates: true,
  },
};

export interface ScenarioRoute extends ScenarioRow {
  readonly scenario: Scenario;
  readonly mapVersion: string;
  /**
   * The ids of the rules that decided it (`SCENARIO_RULE_IDS`), one per
   * pattern that matched, in rule order. An unexplainable route cannot be
   * debugged, and an id explains it as well as the words did. Never the
   * customer's words: until design Q20 (decision D31,
   * docs/DESIGN-learn-qualify-2026-10-03.md) this held each pattern's match
   * text, three patterns carry `.*`, and `routing.decided` wrote it to the
   * event log (CLAUDE.md section 10).
   */
  readonly matched: readonly string[];
  /**
   * How far back the deciding turn was. 0 is the message being answered; 1 is
   * the customer turn before it, and so on. A route inherited from earlier in
   * the thread is a weaker claim than one the current message made on its own,
   * and a route that cannot say which it is cannot be argued with later.
   */
  readonly turnsBack: number;
}

/**
 * Decision list item 56 (TA-413 addendum): one widening pass on the ready-made
 * row, after the 29c measure found most of its misses never routed at all.
 * Only the ready-made half is here: the custom half claimed more threads
 * labelled otherwise than it gained, so it is held and the custom row is
 * exactly v1. The additions follow three rules, each on the precision side:
 *
 * - Every addition is wrapped by this guard. The ready-made row is tested
 *   before `review_ask`, which arms the solicitation-compliance gate, so a
 *   widened phrase never fires on a message that mentions a review or Yelp.
 * - No past tense of the customer's own purchase ("bought", "ordered"): that
 *   is a finished sale, the post-purchase and review moment, not an open
 *   inquiry.
 * - No generic follow-up words (paying, shipping, "I'll take it", a price with
 *   no piece named). Those turns stay `unknown` so they inherit their thread's
 *   row; routing them would flip a custom or repair thread to ready-made on its
 *   payment turn.
 *
 * The v1 patterns are unchanged, so every message they routed routes the same.
 */
const unlessReview = (re: RegExp): RegExp =>
  new RegExp(`(?<!\\b(?:review|yelp)[\\s\\S]*)(?:${re.source})(?![\\s\\S]*\\b(?:review|yelp))`, re.flags);

/** Item 91 (a). U+2018, U+2019 and U+02BC read as an apostrophe, as in botQuestion.ts. */
const APOSTROPHES = /[\u2018\u2019\u02BC]/g;

/** Item 91 (a)'s fold, shared with the reply path's contact checks (item 95a). */
export function foldApostrophes(text: string): string {
  return text.replace(APOSTROPHES, "'");
}

/**
 * TA-464. Words that report something wrong with a piece the customer has:
 * damage, a reaction, a fake, a wrong or missing part, or a customer who will
 * not buy again. Section 9 puts complaints at class D, a human in the loop,
 * and the complaint row only knows its own words ("fell out", "refund"). Item
 * 56 widened the ready-made row to read posts, stories and "buying", which is
 * also how a complaint names the piece it is about, so "the chain you posted
 * snapped" was read as an open purchase and handed to an agent at class B.
 *
 * Kept narrow, so a question asked before buying stays a purchase question:
 * past or ongoing damage only ("tarnished", never "does it tarnish"), no bare
 * "loose" or "chips" (loose diamonds and diamond chips are stock), and
 * "turned my finger green" rather than "green", which is a stone's colour.
 * Returns and exchanges are left out: a return asked about before buying is
 * policy, and after buying the reply path's escalation reads it.
 *
 * Item 88 (a) adds a piece not as described. The comparative is skipped when a
 * question comes first in its sentence ("is it smaller than pictured?"), which
 * is a buyer asking, not a report.
 *
 * Item 93 (a) adds the contraction of "is loose" ("stone's loose", and "its"
 * typed for it) and the body part first ("my finger turned green").
 */
const REPORTS_DAMAGE: readonly RegExp[] = [
  /\b(crack(ed|s)?|chipped|snapped|broke|broken|damaged|defective|faulty)\b/i,
  /\b(came|coming|fell|falling|falls) (off|out|apart|loose)\b/i,
  /\b(is|are|was|were|went|gone|got|getting|feels?) (a bit |a little |really |so |very )?loose\b/i,
  /\b('s|its) (a bit |a little |really |so |very )?loose\b/i,
  /\b(tarnished|tarnishing|discolou?red|discolou?ring|faded|fading|peeled|peeling|rusted|rusting)\b/i,
  /\b(scratched|scratches|dented|dents|bent)\b/i,
  /\bturned (my )?(finger|fingers|skin|neck|wrist|ear|ears)( \w+)? (green|black)\b/i,
  /\b(finger|fingers|skin|neck|wrist|ear|ears) (turned|went|is turning|has turned|is|was|got) (green|black)\b/i,
  /\b(rash|allergic reaction)\b/i,
  /\bmissing (a |the |one |two )?(stone|diamond|piece|link|part|item)s?\b/i,
  /\bwrong (size|item|ring|one|piece|colou?r|order|stone|length)\b/i,
  /\b(never|not|won'?t|wont|don'?t|dont) (be )?(buy|buying|order|ordering|shop|shopping|purchas\w*)\b[\s\S]*\b(again|anymore|any more)\b/i,
  /(?<!\b(?:is|are|will|would|does|do|can|could|should)\s+(?:it|this|that|they|these|those|you|u|there)\b[^.!?]*)\b(smaller|tinier|bigger|larger|thinner|thicker|shorter|lighter|darker|duller)\s+than\s+(advertised|described|pictured|listed|shown|expected|(i|we)\s+(expected|thought)|(it|they)\s+look(ed|s)?|in\s+(the|your)\s+(photos?|pictures?|pics?|videos?|post|listing|ad|story))\b/i,
  /\bnot as (described|advertised|pictured|shown)\b/i,
  /\b(not|isn'?t|wasn'?t) what (i|we) (ordered|paid for)\b/i,
  /\b(looks?|looked) nothing like (the|your) (photos?|pictures?|pics?|videos?|post|listing|ad|story)\b/i,
  /\b(does|did|do)(n'?t| not) look (anything )?like (the|your) (photos?|pictures?|pics?|videos?|post|listing|ad|story)\b/i,
];

/**
 * Exported for decision list item 94(a): a damage report on a class D route
 * gets the reply path's fixed line. Reads a phone's apostrophe as a typed one.
 */
export function reportsDamage(text: string): boolean {
  const folded = foldApostrophes(text);
  return REPORTS_DAMAGE.some((p) => p.test(folded)) || claimsFake(folded);
}

/**
 * TA-1355 D4, Kabir's typed "fix" of 2026-10-17. "fake", "counterfeit",
 * "knock-off" and "not real" were read as damage wherever they stood, so a
 * buyer's trust question before buying ("is lab basically fake?") took item
 * 94(a)'s fixed line. A sentence holding one of them is let go only when it is
 * a question, names nothing the customer owns or bought, and points at no
 * piece with a pronoun: "is it fake?" after a purchase is a complaint. The rest
 * stays damage, so "how do i know it's not fake" still goes to a human.
 * Precision beats recall (B13).
 */
const FAKE_WORD = /\b(fake|counterfeit|knock-?off|not real)\b/i;
const FAKE_QUESTION_OPENER = /^\s*(so )?(is|are|isn't|aren't|how|what|whats|what's|which|can|could|do|does|would|will|should)\b/i;
const OWNED_OR_BOUGHT = /\b(my|mine|our|ours|me|bought|got|ordered|purchased|received|paid|sold|sent|gave|arrived|turned out|appraiser|appraised|jeweler said|jeweller said|picked (?:up|out)|(?:you|y'all|yall) made|made (?:(?:it|them) )?(?:for|us|me)|from (?:your|ur|the) (?:shop|store|place)|at (?:your|ur) (?:shop|store|place))\b/i;
const POINTS_AT_A_PIECE = /\b(it|its|this|that|these|those|they|theyre|them)\b/i;

function claimsFake(folded: string): boolean {
  for (const sentence of folded.split(/(?<=[.!?\n])/)) {
    if (!FAKE_WORD.test(sentence)) continue;
    const question = /\?\s*$/.test(sentence) || FAKE_QUESTION_OPENER.test(sentence);
    if (!question || OWNED_OR_BOUGHT.test(sentence) || POINTS_AT_A_PIECE.test(sentence)) return true;
  }
  return false;
}

/**
 * TA-1355 D4 fix (final review of staged/reply-accuracy-2026-10-17). D4's
 * ruling keeps a claim about the customer's own piece as damage, and the piece
 * is often named a turn earlier: "i bought an engagement ring from you last
 * month", then "is the ring fake?". Read alone, the second is a trust question
 * and its thread's class C row composed; under v9 it was damage, class D. So a
 * message with a fake word is damage when any earlier customer turn names
 * something the customer owns or bought (`OWNED_OR_BOUGHT`). A trust question
 * on a thread that names no purchase keeps D4's reading. Precision beats
 * recall (B13): a human reads the rare shopper this catches.
 *
 * `priorCustomerTurns` are the turns `routeConversation` reads, oldest first.
 */
export function reportsDamageInThread(question: string, priorCustomerTurns: readonly string[]): boolean {
  if (reportsDamage(question)) return true;
  if (!FAKE_WORD.test(foldApostrophes(question))) return false;
  return priorCustomerTurns.some((t) => OWNED_OR_BOUGHT.test(foldApostrophes(t)));
}

interface RulePattern {
  /**
   * Stable within its row; the recorded id is `<scenario>.<id>`. A renamed id
   * orphans every `routing.decided` row that recorded it, so the full list is
   * pinned by `routingCustody.test.ts`.
   */
  readonly id: string;
  /**
   * Named groups, where a pattern has them, name its alternatives: the id
   * recorded is `<scenario>.<id>.<group>` for the group that matched, so a
   * reader can still tell "engagement ring" from "wedding band set" without
   * the words.
   */
  readonly re: RegExp;
}

/**
 * Ordered, and the order is the whole design. A message can be a purchase AND
 * a fraud signal, a question AND a complaint. The first rule that matches
 * wins, so the rows that must never be missed are tested first, and the
 * broadest commercial rows last.
 *
 * Eleven of section 9's thirteen rows appear below. The two that do not,
 * `ghost_revival` and `post_purchase_care`, are outbound plays rather than
 * readings of an inbound message, so there is nothing for a rule to match on
 * (TA-315). This list is the whole set of scenarios a customer message can
 * produce, `unknown` aside.
 */
const RULES: readonly {
  readonly scenario: Scenario;
  readonly patterns: readonly RulePattern[];
  /** A message this matches skips the row, whatever its patterns say. */
  readonly unless?: (text: string) => boolean;
}[] = [
  {
    // Section 9 puts this at D and T3. Freeze and hand to a human, never
    // accuse. It leads because a fraud signal wrapped in a friendly purchase
    // inquiry is the normal shape of one.
    scenario: "fraud_suspect",
    patterns: [
      { id: "stolen", re: /\bstolen\b/i },
      { id: "other_party_payment", re: /\b(someone else|third party|different) (card|account)\b/i },
      { id: "chargeback", re: /\bchargeback\b/i },
      { id: "western_union", re: /\bwestern union\b/i },
      { id: "gift_card_payment", re: /\bgift ?cards?\b.*\bpay\b|\bpay\b.*\bgift ?cards?\b/i },
      { id: "ship_elsewhere", re: /\bship (it )?to a different (address|country)\b/i },
      { id: "overpayment", re: /\boverpay(ment)?\b/i },
    ],
  },
  {
    scenario: "complaint_warranty",
    patterns: [
      { id: "repeat_failure", re: /\b(fell out|fallen out|broke again|broken again|keeps breaking)\b/i },
      { id: "displeasure", re: /\b(unhappy|disappointed|furious|angry|upset)\b/i },
      // Item 88 (a). A conditional ("won't be happy", "would not be happy") is not one.
      { id: "not_happy", re: /\bnot (very |really |too |so |that |super |particularly |at all )?happy\b/i },
      { id: "isnt_happy", re: /\b(is|was|are|were|ai)n'?t (very |really |too |so |that |super |particularly )?happy\b/i },
      { id: "refund_or_complaint", re: /\b(refund|money back|complaint|complain)\b/i },
      { id: "warranty_claim", re: /\b(warranty|guarantee)\b.*\b(claim|cover|honou?r)\b/i },
      { id: "second_time", re: /\bsecond time\b/i },
    ],
  },
  {
    scenario: "price_challenge",
    patterns: [
      { id: "discount_ask", re: /\b(discount|deal|best price|better price|match (that|the) price|price match)\b/i },
      { id: "come_down_ask", re: /\b(do any better|come down|knock (some|anything) off|too expensive|cheaper)\b/i },
      { id: "percent_off", re: /\b\d{1,2} ?(%|percent) off\b/i },
      { id: "haggle", re: /\bhaggle\b/i },
    ],
  },
  {
    scenario: "educated_buyer_debate",
    // v10 (TA-1355): a damage report that names its stone ("you sold me a fake
    // lab grown diamond") is a complaint for a human, not a debate. It routed
    // here, class C, and composed. The same guard as the custom and ready-made rows.
    unless: reportsDamage,
    patterns: [
      { id: "lab_grown", re: /\blab[- ]?(grown|created)\b/i },
      { id: "simulant_or_process", re: /\b(moissanite|cvd|hpht)\b/i },
      { id: "natural_or_mined", re: /\b(natural|mined) (diamond|stone)s?\b/i },
      { id: "certification", re: /\b(certif(ied|icate)|gia|igi)\b/i },
      { id: "resale_value", re: /\b(resale|resell) value\b/i },
    ],
  },
  {
    scenario: "consult_confirm",
    patterns: [
      { id: "booking_word", re: /\b(appointment|consult(ation)?|booking)\b/i },
      // Item RSCH. The stem "reschedul" sat in a group closed by \b, so it needed
      // a word boundary right after the l and no inflection ever matched.
      // Now the word and its four forms, with or without the hyphen; the row's
      // other alternatives are as they were.
      { id: "change_or_late", re: /\b(re-?schedul(?:e|es|ed|ing)|confirm my|running late|cant make it|can'?t make it)\b/i },
    ],
  },
  {
    scenario: "repair_inquiry",
    patterns: [
      // TA-1355 D8: a change of size ("size 6, needs to go to a 7", "go up a size") is a resize.
      // D8 fix: the size it goes to is a ring size, 3 to 16 or a half size
      // between, and "up to" a number is a budget ("size 6, budget up to 2000"), never a resize.
      { id: "resize", re: /\b(resiz(e|ing)|size (up|down)|siz(e|ing) (a|my|this))\b|\b(go|goes|going|be|move|take it|bring it) (up|down) (a|one|half a|half|two) sizes?\b|\bsize \d+(\.\d+)?\b[^.?!]{0,30}?\b(to|be) (a )?(size )?(?<!\bup to )(1[0-6]|[3-9])(\.5)?\b(?!\.\d)(?! ?(carat|ct|mm|weeks?|days?|k)\b)/i },
      { id: "repair_work", re: /\b(repair|fix|mend|restring|re-?tip|solder|polish|refinish|rhodium)\b/i },
      { id: "part", re: /\b(battery|clasp|prong|chain break|link removed?)\b/i },
      { id: "clean_or_inspect", re: /\b(clean(ing)?|inspect(ion)?)\b.*\b(ring|chain|watch|piece)\b/i },
    ],
  },
  {
    scenario: "custom_engagement",
    // v8 fix 1 (TA-464): a damage report ("the engraving came off") is not a
    // custom request. Without this the row claimed it before the damage check,
    // which only runs on a message that routes unknown, and it skipped class D.
    unless: reportsDamage,
    patterns: [
      { id: "custom_work", re: /\b(custom|bespoke|design (a|my)|make me|from scratch)\b/i },
      // Was `(engagement ring|propos(e|al)|wedding band set)`. The same
      // alternatives in the same order, so the first match is the same; each
      // is now a named group so the id says which one it was (design Q20).
      { id: "occasion", re: /\b(?:(?<engagement_ring>engagement ring)|(?<propose>propose)|(?<proposal>proposal)|(?<wedding_band_set>wedding band set))\b/i },
      { id: "reset_or_remount", re: /\b(reset|remount) (my|the|her|his) (stone|diamond)\b/i },
      // v8 (loop o O2, decision row 248 item 2), tightened by fix 1. Four
      // vocabulary misses found by the never-routed finder, tested against the
      // cached labels. Each is appended after the rows above, so a message they
      // already matched keeps its first matching id. A sentence that hits one
      // of the new patterns and an older one now records more than one id, and
      // the legacy-parity battery in routingCustody.test.ts does not cover
      // those sentences. The patterns are request-shaped on purpose: a bare
      // "engraving", "initials" or "customs" is a warranty, care or shipping
      // question as often as a custom request. custom_inflection leaves out the
      // forms custom_work already reads ("custom", "custom-made").
      { id: "custom_inflection", re: /\b(customi[sz](e|ed|es|ing|ation)|custommade)|\b(?:(?:you|u|ya)(?: guys)? (?:do|make|making)|your|ur) customs\b(?! (?:fees?|charges?|dut(?:y|ies)|tax(?:es)?|clearance|forms?)\b)/i },
      { id: "have_it_made", re: /\b(?:have|get) (?:it|one|this|that|a \w+) (?:made|designed)\b(?! (?:smaller|bigger|larger|shorter|longer|tighter|looser|in \d|in (?:white|yellow|rose|10k|14k|18k|silver|gold|platinum)))|\bmade for me\b|\bmy own design\b/i },
      { id: "engrave_or_monogram", re: /\b(?:(?:can|could|would|do|does) (?:you|u|ya|it|this|that|they)(?: guys)?(?: be)? (?:engrave|engraved|monogram|monogrammed)|(?:get|have|want|like|add|need)(?: it| them| this| that| one| mine)? (?:engraved|monogrammed)|(?:engraved|monogrammed) with|(?:an? |add |want |like )(?:engraving|monogram)s?|name (?:necklace|chain|ring)s?)\b/i },
      { id: "can_you_make", re: /\b(?:can|could|would) (?:you|u|ya)(?: guys)? (?:make|design|create) (?:me |us |her |him )?(?:a|an|one|something)(?: (?!of\b|the\b|my\b|for\b)\w+){0,3}? (?:ring|band|chain|pendant|necklace|bracelet|piece|grill|grillz|earrings?|charm|anklet|cross|medallion)s?\b/i },
    ],
  },
  {
    scenario: "vendor_spam",
    patterns: [
      { id: "agency_pitch", re: /\b(seo|marketing services|our agency|pricing deck|partnership opportunity)\b/i },
      { id: "service_pitch", re: /\b(we (offer|provide|specialize)|increase your (sales|traffic|followers))\b/i },
      { id: "wholesale_supplier", re: /\b(bulk|wholesale) (supplier|manufactur)/i },
    ],
  },
  {
    // Last of the commercial rows on purpose. Almost every jewellery message
    // mentions buying something; reading that as a purchase before the rows
    // above would swallow the haggle, the debate and the complaint.
    scenario: "ready_made_purchase",
    unless: reportsDamage,
    patterns: [
      { id: "availability_or_price", re: /\b(in stock|do you have|available|how much (is|for)|price (of|on)|cost of)\b/i },
      { id: "buy_word", re: /\b(buy|purchase|order)\b/i },
      { id: "hold_this", re: /\b(hold|reserve) (it|this|that)\b/i },
      // Item 56. A piece the shop already has: asked after, pointed at in a
      // post or story, held, or bought. "do you still have my ring" is a
      // pickup, not a purchase, so a possessive after the verb does not fire.
      ...[
        { id: "do_you_have", re: /\bdo (you|u|ya|y'?all)( guys| all)?( still| currently)? (have|carry|sell|stock|got)\b(?! (my|her|his|our|me)\b)/i },
        { id: "still_have", re: /\b(you|u|ya|y'?all) still (have|got) (it|this|that|these|those|them|any|one|the)\b/i },
        { id: "for_sale_or_sold_out", re: /\b(for sale|sold out)\b/i },
        { id: "is_it_sold", re: /\b(is|was|has) (it|this|that|this one|that one) (already )?(been )?sold\b/i },
        { id: "your_post", re: /\b(your|ur) (last |latest |recent |new |newest )?(story|stories|post|posts|reel|reels|live|highlights?|listing|listings)\b/i },
        { id: "you_posted", re: /\b(you|u) (just )?(posted|listed|uploaded)\b/i },
        { id: "saw_it_on_yours", re: /\bsaw (it|this|that|these|those|them|one|this one|that one) (on|in) (your|ur)\b/i },
        { id: "interested_in", re: /\binterested in (this|that|these|those|it|them|this one|that one|one)\b/i },
        { id: "buying_word", re: /\b(buying|purchasing|ordering)\b/i },
        { id: "hold_these", re: /\b(hold|reserve) (them|these|those|one|this one|that one)\b/i },
        { id: "put_on_hold", re: /\bput (it|this|that|them|these|those|one) (on hold|aside)\b/i },
        { id: "set_aside", re: /\bset (it|this|that|them|these|those|one) aside\b/i },
        { id: "comes_in", re: /\b(does|do) (it|this|that|these|those|they|this one|that one) come in\b/i },
        { id: "ready_to_ship", re: /\bready to ship\b/i },
        { id: "how_much_are_these", re: /\bhow much (are|were) (these|those|they|them)\b/i },
        { id: "what_size_is_it", re: /\bwhat (size|carat|karat|length|ct)( weight)? (is|are) (it|this|that|these|those|they|this one|that one)\b/i },
        // v9 (TA-1346, Kabir's typed 2026-10-16 go on the funnel audit's H2):
        // the opener a wealthy, serious buyer actually types names an occasion,
        // a person and a figure, not a piece the shop posted. "I'm looking for
        // an anniversary gift for my wife, budget around 5k" routed `unknown`,
        // class D, and waited for a human. Three request-shaped patterns, each
        // appended after the rows above so an earlier match keeps its id.
        // `budget_stated` wants a stated amount or figure: "out of my budget"
        // and "over budget" are a price conversation, and still route unknown
        // (B13: a human, not a guess) unless the price row read them first.
        { id: "gift_for", re: /\b(gift|present|something) for (my|her|his|our|the|a) (wife|husband|girlfriend|boyfriend|fiancee?|partner|mom|mum|mother|dad|father|daughter|son|sister|brother|parents|anniversary|birthday|graduation|wedding|holidays?|christmas|valentines?)\b/i },
        // `looking_for` wants a piece named within three words: item 56 ruled
        // that a bare "im just looking for a gift" stays unknown, and it does.
        { id: "looking_for", re: /\b(looking|shopping|searching|hunting) for (a|an|some|the perfect)(?: \w+){0,3}? (ring|band|bracelet|chain|necklace|pendant|earrings?|studs?|hoops?|watch|piece|cuban|tennis|eternity|anklet|charm|cross|medallion|grillz?|link|solitaire|diamond)s?\b|\bin the market for\b/i },
        { id: "budget_stated", re: /\bbudget (is|of|around|about|under|up to|is around|is about|max)\b|\bbudget\s*[:~]?\s*\$?\d/i },
      ].map((p) => ({ id: p.id, re: unlessReview(p.re) })),
    ],
  },
  {
    scenario: "review_ask",
    patterns: [{ id: "review_word", re: /\b(leave a review|google review|yelp)\b/i }],
  },
];

/**
 * The named groups a regex source declares, in source order. The one reading
 * of "a named group" that the router, the id list and the structural test in
 * `routingCustody.test.ts` all share. A name is a JS identifier in ASCII
 * (`[A-Za-z_$][\w$]*`), so `(?<=` and `(?<!` are lookbehinds, never names. A
 * name that is not a code (mixed case, a digit) is read all the same and
 * yields an id that fails the id format check, so it is caught loudly instead
 * of being unreadable here and recorded as a bare id.
 */
export function declaredGroupNames(source: string): string[] {
  return [...source.matchAll(/\(\?<([A-Za-z_$][\w$]*)>/g)].map((m) => m[1] as string);
}

/**
 * The ids one pattern can record: `<scenario>.<id>`, or one
 * `<scenario>.<id>.<group>` per named group. Exported, with `matchedRuleId`
 * and `declaredGroupNames`, so `routingCustody.test.ts` can drive a synthetic
 * rule through the router's own reading; nothing else calls them.
 */
export function patternRuleIds(scenario: string, p: { readonly id: string; readonly re: RegExp }): string[] {
  const groups = declaredGroupNames(p.re.source);
  const base = `${scenario}.${p.id}`;
  return groups.length === 0 ? [base] : groups.map((g) => `${base}.${g}`);
}

/**
 * The id a pattern records when it matches `text`, or null when it does not.
 * Only the id leaves: the match itself, and every group's text, are dropped
 * here (design Q20, decision D31; CLAUDE.md section 10).
 */
export function matchedRuleId(scenario: string, p: { readonly id: string; readonly re: RegExp }, text: string): string | null {
  const m = p.re.exec(text);
  if (m === null) return null;
  const base = `${scenario}.${p.id}`;
  const groups = declaredGroupNames(p.re.source);
  if (groups.length === 0) return base;
  const hit = groups.find((g) => m.groups?.[g] !== undefined);
  // Unreachable while every way through a grouped pattern sets a named group,
  // which `routingCustody.test.ts` reads off each pattern's structure. If a
  // pattern ever matches with none set, the bare id is not in
  // SCENARIO_RULE_IDS: the writer withholds it, counts it and alerts.
  return hit === undefined ? base : `${base}.${hit}`;
}

/**
 * Every id a route can record in `matched`, in rule order. Design row Q20,
 * decision D31 (docs/DESIGN-learn-qualify-2026-10-03.md): `routing.decided`
 * records these and nothing else, because the event log is no place for the
 * customer's words (CLAUDE.md section 10). Pinned by `routingCustody.test.ts`.
 * The names are the builder's: a rename after rows accumulate orphans every
 * row that recorded the old name, so they are Kabir's to rename first.
 * AWAITING RATIFICATION (design D31, docs/DESIGN-learn-qualify-2026-10-03.md)
 */
export const SCENARIO_RULE_IDS: readonly string[] = RULES.flatMap((rule) =>
  rule.patterns.flatMap((p) => patternRuleIds(rule.scenario, p)),
);

/**
 * Each rule pattern's source, by `<scenario>.<id>`, in rule order: read-only
 * text, never the live RegExp. `routingCustody.test.ts` reads it to hold that
 * a grouped pattern sets one of its named groups on every match, the one thing
 * the pin and the vectors cannot see (design Q20, review round 1).
 */
export const SCENARIO_RULE_SOURCES: readonly { readonly pattern: string; readonly source: string }[] = Object.freeze(
  RULES.flatMap((rule) =>
    rule.patterns.map((p) => Object.freeze({ pattern: `${rule.scenario}.${p.id}`, source: p.re.source })),
  ),
);

const RULE_ID_SET: ReadonlySet<string> = new Set(SCENARIO_RULE_IDS);

/** Whether a value is exactly one of `SCENARIO_RULE_IDS`. No trimming, no case folding: an id is a code. */
export function isScenarioRuleId(value: unknown): value is string {
  return typeof value === "string" && RULE_ID_SET.has(value);
}

/**
 * Reads a stored `routing.decided` `matched` without ever handing back a
 * customer's words. A row written since design Q20 holds rule ids, which come
 * back whole; a row written before it holds the matched words, and the event
 * log is append-only (CLAUDE.md section 5), so those rows stay as written and
 * every entry that is not a known id is counted in `withheld`, never returned.
 * An absent value (undefined or null) is nothing; any other value that is not
 * a list is one withheld value. The writer screens with this before it
 * writes, and a reader that shows a payload screens with it before it shows.
 */
export function routingMatchedRuleIds(matched: unknown): { readonly ruleIds: readonly string[]; readonly withheld: number } {
  if (matched === undefined || matched === null) return { ruleIds: [], withheld: 0 };
  if (!Array.isArray(matched)) return { ruleIds: [], withheld: 1 };
  const ruleIds = matched.filter(isScenarioRuleId);
  return { ruleIds, withheld: matched.length - ruleIds.length };
}

export function routeScenario(question: string): ScenarioRoute {
  return routeMessage(question, false);
}

/**
 * `threadDamage` is `reportsDamageInThread`'s answer for this message: when
 * true, every row guarded by `unless` is skipped as though the message itself
 * read as damage (TA-1355 D4 fix).
 */
function routeMessage(question: string, threadDamage: boolean): ScenarioRoute {
  const text = foldApostrophes(question.trim());
  if (text.length === 0) {
    return {
      scenario: "unknown",
      ...SCENARIO_MAP.unknown,
      mapVersion: SCENARIO_MAP_VERSION,
      matched: [],
      turnsBack: 0,
    };
  }

  for (const rule of RULES) {
    if (rule.unless !== undefined && (threadDamage || rule.unless(text))) continue;
    const matched = rule.patterns
      .map((p) => matchedRuleId(rule.scenario, p, text))
      .filter((m): m is string => m !== null);
    if (matched.length > 0) {
      return {
        scenario: rule.scenario,
        ...SCENARIO_MAP[rule.scenario],
        mapVersion: SCENARIO_MAP_VERSION,
        matched,
        turnsBack: 0,
      };
    }
  }

  return {
    scenario: "unknown",
    ...SCENARIO_MAP.unknown,
    mapVersion: SCENARIO_MAP_VERSION,
    matched: [],
    turnsBack: 0,
  };
}

/**
 * Route a message in the conversation it belongs to.
 *
 * `routeScenario` reads one message, and most turns in a real thread are
 * follow-ups that carry none of their own keywords: "and how long does that
 * take" routes to `unknown` and loads no framework at all, on a thread whose
 * subject has been a repair since the first line. Routing a message in
 * isolation is the same defect as composing a reply in isolation, one slot
 * over.
 *
 * The current message always wins when it routes on its own, so a repair
 * thread that turns into a price argument is a price argument now. Only when
 * it says nothing does the nearest earlier customer turn decide, and the
 * result records how far back that was.
 *
 * `priorCustomerTurns` is oldest first, and should be the turns the MODEL can
 * see. Routing off history the prompt does not carry would produce a strategy
 * the model has no evidence for.
 */
export function routeConversation(
  question: string,
  priorCustomerTurns: readonly string[],
): ScenarioRoute {
  // TA-1355 D4 fix: a fake word after a purchase turn is damage, so the rows
  // that skip a damage report skip it here too ("is the lab grown diamond
  // fake?" after buying one is not a debate).
  const damage = reportsDamageInThread(question, priorCustomerTurns);
  const direct = routeMessage(question, damage);
  if (direct.scenario !== "unknown") return direct;
  // TA-464. "it arrived cracked" on a thread that began as a purchase is the
  // commonest complaint there is, and it carries no row's words. A damage
  // report only ever lands in class D: it keeps a class D row its thread
  // already has (a fraud flag keeps its freeze), and otherwise stays unknown.

  for (let i = priorCustomerTurns.length - 1; i >= 0; i--) {
    const prior = routeScenario(priorCustomerTurns[i] ?? "");
    if (prior.scenario === "unknown") continue;
    if (damage && prior.graduationClass !== "D") return direct;
    return { ...prior, turnsBack: priorCustomerTurns.length - i };
  }
  return direct;
}
